import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Brain, Activity, Shield, Radar, TrendingUp, ChevronLeft, ArrowRight, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

const auditData = [
  {
    id: "cognitive",
    title: "Cognitive Bandwidth",
    subtitle: "The Focus Audit",
    icon: <Brain className="h-4 w-4" />,
    desc: "In an average week, what percentage of your mental \"operating system\" is consumed by decoding silence, analyzing text subtext, or managing the anxiety caused by a partner's inconsistency?",
    rubric: [
      { range: "1–5", text: "My mind is clear; the relationship provides a stable 'Home Base' for my work." },
      { range: "6–10", text: "Occasional distractions, but I can quickly return to focus." },
      { range: "11–15", text: "I am consistently 'background processing' relationship stress during professional hours." },
      { range: "16–20", text: "High cognitive drain; I am operating at a fraction of my professional capacity due to mental loops." }
    ]
  },
  {
    id: "biological",
    title: "Biological Regulation",
    subtitle: "The Nervous System Audit",
    icon: <Activity className="h-4 w-4" />,
    desc: "When you experience conflict or distance, can you maintain access to your logical, executive self, or does your nervous system \"hijack\" you—resulting in chest tightness, \"brain fog,\" or an obsessive need to \"fix\" the situation?",
    rubric: [
      { range: "1–5", text: "I remain grounded; I can communicate clearly and safely." },
      { range: "6–10", text: "I feel the stress, but I can self-regulate within a few hours." },
      { range: "11–15", text: "Prolonged states of 'Fight/Flight'; it takes days to feel physically normal again." },
      { range: "16–20", text: "Executive Hijack; I feel biologically 'addicted' to the resolution of the conflict." }
    ]
  },
  {
    id: "identity",
    title: "Identity Alignment",
    subtitle: "The Integrity Audit",
    icon: <Shield className="h-4 w-4" />,
    desc: "Do you find yourself \"negotiating\" your standards, silencing your needs, or editing your personality to maintain the connection? Is the relationship an asset to your growth, or a liability to your sense of self?",
    rubric: [
      { range: "1–5", text: "I feel fully seen and expanded; my standards are respected." },
      { range: "6–10", text: "Minor compromises, but my core identity remains intact." },
      { range: "11–15", text: "I am playing a 'Version 1.0' of myself to stay safe in the relationship." },
      { range: "16–20", text: "Systematic Self-Abandonment; I no longer recognize the woman I've become." }
    ]
  },
  {
    id: "radar",
    title: "Familiarity Homeostasis",
    subtitle: "The Radar Audit",
    icon: <Radar className="h-4 w-4" />,
    desc: "When you encounter a partner who is consistent, clear, and emotionally available, does your system register \"Safety\" or does it register \"Boredom\"?",
    rubric: [
       { range: "1–5", text: "Consistency is attractive and necessary for me." },
       { range: "6–10", text: "I appreciate stability but occasionally miss the 'spark' of uncertainty." },
       { range: "11–15", text: "I struggle to feel chemistry without a degree of 'chase' or anxiety." },
       { range: "16–20", text: "Radar Malfunction; I am biologically attracted to unavailability and repelled by security." }
    ]
  },
  {
    id: "equity",
    title: "Future Equity",
    subtitle: "The 10-Year Projection",
    icon: <TrendingUp className="h-4 w-4" />,
    desc: "If you continue to invest your emotional capital into this current pattern for the next ten years, what is the projected state of your health, your professional legacy, and your peace of mind?",
    rubric: [
        { range: "1–5", text: "High-growth projection; the relationship is an accelerant for my life." },
        { range: "6–10", text: "Maintenance mode; it will be a neutral factor in my long-term success." },
        { range: "11–15", text: "Depreciating asset; the stress will likely impact my long-term health or wealth." },
        { range: "16–20", text: "Bankruptcy risk; staying in this pattern will result in the erosion of my life's work." }
    ]
  }
];

import { dbService } from '../services/dbService';
import { AFFILIATE_LINKS } from '../constants/affiliates';
import { auth } from '../lib/firebase';

export default function PsychologyAudit() {
  const [isStarted, setIsStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [scores, setScores] = useState<string[]>(Array(5).fill(''));
  const [isFinished, setIsFinished] = useState(false);

  const handleScoreChange = (val: string) => {
    let newScores = [...scores];
    
    if (val === '') {
        newScores[currentStep] = '';
    } else {
        let num = parseInt(val);
        if (num < 1) num = 1;
        if (num > 20) num = 20;
        newScores[currentStep] = num.toString();
    }
    
    setScores(newScores);
  };

  const handleNext = async () => {
    if (currentStep < auditData.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsFinished(true);

      // Save to DB
      try {
        const user = auth.currentUser;
        if (!user) throw new Error("User not found");

        const totalScore = scores.reduce((sum, val) => sum + (parseInt(val) || 0), 0);
        let diagnostic = "Crisis Mode";
        let title = "The Systemic Breach";
        if (totalScore <= 30) {
            diagnostic = "Asset Mode";
            title = "The Secure Baseline";
        } else if (totalScore <= 60) {
            diagnostic = "Maintenance Mode";
            title = "The High-Functioning Leak";
        }

        await dbService.saveAuditResult(user.uid, {
          auditType: 'attachment_roi',
          title: 'Attachment ROI Audit',
          diagnostic: `${diagnostic}: ${title}`,
          summary: `Neuro-biological diagnostic complete. Baseline Score: ${totalScore}/100.`
        });
      } catch (err) {
          console.error("Failed to persist audit results:", err);
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
     setCurrentStep(prev => prev - 1);
     window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalScore = scores.reduce((sum, val) => sum + (parseInt(val) || 0), 0);
  const isInputValid = scores[currentStep] !== '' && parseInt(scores[currentStep]) >= 1 && parseInt(scores[currentStep]) <= 20;

  if (!isStarted) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-6 bg-theme-bg text-theme-text">
          <div className="max-w-3xl mx-auto text-center mb-16">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                  <div className="w-20 h-20 bg-theme-border/30 flex items-center justify-center mx-auto mb-8 border border-theme-border">
                    <Lock className="h-8 w-8 text-gold" />
                  </div>
                  <h1 className="font-serif text-4xl md:text-6xl font-bold tracking-tight mb-8 uppercase leading-tight">The Attachment <br/> ROI Framework</h1>
                  <div className="w-24 h-1 bg-gold mx-auto mb-8" />
                  <h2 className="font-sans text-sm md:text-lg tracking-widest font-light uppercase text-theme-text mb-12">A Neuro-Biological Diagnostic <br/> for the High-Achieving Woman</h2>
                  
                  <div className="text-left space-y-6 text-theme-text/80 text-sm md:text-base leading-relaxed border-t border-theme-border pt-12 mt-12">
                      <h4 className="font-serif text-2xl mb-8 italic text-gold text-center">The High-Performance Blind Spot</h4>
                      <p>In your professional life, you are a master of high-stakes decision-making. You understand resource allocation, risk mitigation, and the necessity of a high return on investment (ROI). However, for many high-achieving women, the very executive functions that drive career success are often bypassed by a more primitive system in the context of intimacy.</p>
                      <p>This is not a failure of character; it is a matter of neuro-biological calibration. When your relationship operates in a state of chronic uncertainty, it doesn't just hurt—it costs. It costs mental bandwidth, physiological health, and future equity.</p>
                      <p className="text-gold font-medium italic mt-6 border-l-4 border-gold pl-6 py-2">This diagnostic is designed to help you quantify the "Emotional Overhead" of your current attachment pattern and provide a clear roadmap for recalibration.</p>
                  </div>
                  
                  <div className="mt-16">
                      <button onClick={() => setIsStarted(true)} className="btn-gold px-12 py-4">Begin Diagnostic Audit</button>
                  </div>
                  <p className="font-sans text-taupe text-[10px] tracking-[0.2em] uppercase font-bold mt-12">Know Yourself. Choose Better.</p>
              </motion.div>
          </div>
      </div>
    );
  }

  if (isFinished) {
      return (
          <div className="min-h-screen pt-32 pb-24 px-6 bg-theme-bg text-theme-text">
            <div className="max-w-4xl mx-auto">
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
                    <header className="flex flex-col md:flex-row justify-between md:items-end mb-12 border-b border-theme-border pb-6 gap-6">
                        <div>
                            <h3 className="font-serif text-3xl font-bold uppercase tracking-wide mb-2">The Results</h3>
                            <p className="text-gold text-[10px] tracking-widest uppercase font-bold">The Attachment ROI Scorecard</p>
                        </div>
                        <div className="flex flex-col md:items-end">
                            <span className="font-sans text-taupe text-[10px] tracking-widest uppercase mb-2">Total Diagnostic Score</span>
                            <div className="text-5xl font-serif font-bold text-theme-text font-variant-numeric: tabular-nums">
                                <span>{totalScore}</span> <span className="text-xl text-taupe font-normal">/ 100</span>
                            </div>
                        </div>
                    </header>

                    <div className="space-y-6 mb-16">
                        <div className={`p-8 border transition-all duration-300 ${totalScore <= 30 ? 'border-gold bg-gold/5 scale-[1.02] shadow-xl' : 'border-theme-border opacity-50'}`}>
                            <div className="flex justify-between items-baseline mb-4">
                                <h4 className="font-serif text-xl font-bold uppercase">0–30: The Secure Baseline</h4>
                                <span className={`text-[10px] font-bold uppercase tracking-widest ${totalScore <= 30 ? 'text-gold' : 'text-taupe'}`}>Asset Mode</span>
                            </div>
                            <p className="text-sm text-theme-text/80 leading-relaxed">Your relationship is a net positive. It provides the psychological safety required for high-performance living. Your primary task is to protect this foundation and continue fine-tuning your communication to ensure long-term stability.</p>
                        </div>

                        <div className={`p-8 border transition-all duration-300 ${totalScore > 30 && totalScore <= 60 ? 'border-theme-text bg-theme-text/5 scale-[1.02] shadow-xl' : 'border-theme-border opacity-50'}`}>
                            <div className="flex justify-between items-baseline mb-4">
                                <h4 className="font-serif text-xl font-bold uppercase">31–60: The High-Functioning Leak</h4>
                                <span className={`text-[10px] font-bold uppercase tracking-widest ${totalScore > 30 && totalScore <= 60 ? 'text-theme-text' : 'text-taupe'}`}>Maintenance Mode</span>
                            </div>
                            <p className="text-sm text-theme-text/80 leading-relaxed">You are successful despite your relationship, not because of it. You are currently paying a "Volatility Tax"—a subtle but persistent drain on your mental and physical resources. You are likely "Success-Shaming" yourself. This is the stage where recalibration is most effective.</p>
                        </div>

                        <div className={`p-8 border transition-all duration-300 ${totalScore > 60 ? 'border-red-800 bg-red-900/10 scale-[1.02] shadow-xl' : 'border-theme-border opacity-50'}`}>
                            <div className="flex justify-between items-baseline mb-4">
                                <h4 className="font-serif text-xl font-bold uppercase">61–100: The Systemic Breach</h4>
                                <span className={`text-[10px] font-bold uppercase tracking-widest ${totalScore > 60 ? 'text-red-500' : 'text-taupe'}`}>Crisis Mode</span>
                            </div>
                            <p className="text-sm text-theme-text/80 leading-relaxed">Your nervous system is in chronic "Survival Mode." The relationship has become a liability that is actively sabotaging your health and executive function. You are currently experiencing the "Sunk Cost Fallacy." Immediate intervention and a radical shift in your internal radar are required.</p>
                        </div>
                    </div>

                    <div className="border border-gold p-8 md:p-12 bg-gold/5 text-center mt-12 mb-12">
                        <h4 className="font-serif text-xl font-bold uppercase tracking-widest mb-6">Clinical Directive</h4>
                        <p className="text-theme-text/80 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-10">
                            {totalScore <= 30 
                                ? "Your ecosystem is structurally sound. Access the Member Dashboard to review advanced execution metrics and continue your growth."
                                : "If your diagnostic score falls within the Maintenance Mode or Crisis Mode, your next step is the immediate stabilization of your nervous system and plugging the emotional ROI leaks."}
                        </p>
                        
                        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
                             <a href={AFFILIATE_LINKS.ONLINE_THERAPY} target="_blank" rel="noopener noreferrer" className="btn-gold px-8 py-3 text-xs flex-1">Online-Therapy.com</a>
                             <a href={AFFILIATE_LINKS.BOOKS.ATTACHED} target="_blank" rel="noopener noreferrer" className="btn-outline px-8 py-3 text-xs flex-1">Executive Study: Attached</a>
                        </div>

                        <Link to="/recalibration" className="text-[10px] uppercase tracking-widest text-gold border-b border-gold/30 pb-1 hover:border-gold transition-all">
                            {totalScore <= 30 ? "Access Member Dashboard" : "Access Phase 2: Recalibration Worksheet"}
                        </Link>
                    </div>
                </motion.div>
            </div>
          </div>
      );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 bg-theme-bg text-theme-text flex flex-col">
      <div className="max-w-3xl mx-auto w-full flex-grow">
        <div className="mb-12">
            <div className="flex items-end justify-between font-serif mb-6 border-b border-theme-border pb-4">
                <span className="text-xl font-bold uppercase tracking-wide">Diagnostic Assessment</span>
                <span className="text-xs text-taupe block italic">1 (Optimal) — 20 (Crisis)</span>
            </div>
            
            <div className="flex space-x-2 mb-8">
                {auditData.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 transition-all duration-500 ${i <= currentStep ? 'bg-gold' : 'bg-theme-border'}`}
                  />
                ))}
            </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] as any }}
          >
            <div className="mb-8 flex items-center space-x-3 text-gold">
                {auditData[currentStep].icon}
                <span className="text-sm uppercase tracking-[0.2em] font-bold">{auditData[currentStep].title}</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-serif mb-6 italic leading-tight text-theme-text">
              {auditData[currentStep].subtitle}
            </h1>
            
            <p className="text-theme-text/80 mb-10 text-sm md:text-base leading-relaxed p-6 bg-theme-bg/50 border border-theme-border border-l-4 border-l-gold">
                {auditData[currentStep].desc}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
              {auditData[currentStep].rubric.map((r, i) => (
                <div key={i} className="flex flex-row items-baseline gap-3 p-5 border border-theme-border bg-theme-border/10">
                  <span className="font-serif font-bold text-gold text-lg shrink-0 w-14">{r.range}</span>
                  <span className="text-theme-text/80 text-xs flex-1 leading-relaxed">{r.text}</span>
                </div>
              ))}
            </div>

            <div className="flex justify-end items-center mb-12 bg-theme-border/10 p-6 border border-theme-border">
                <label htmlFor="scoreInput" className="font-serif italic mr-6 text-lg">Your Score (1-20):</label>
                <input
                    id="scoreInput"
                    type="number"
                    min="1"
                    max="20"
                    value={scores[currentStep]}
                    onChange={(e) => handleScoreChange(e.target.value)}
                    className="w-24 h-16 bg-theme-bg border-2 border-gold focus:ring-4 focus:ring-gold/20 text-center font-serif text-3xl font-bold text-theme-text focus:outline-none transition-all placeholder:text-taupe/30"
                    placeholder="-"
                />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Controls */}
      <div className="max-w-3xl mx-auto w-full mt-auto border-t border-theme-border pt-8 flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="flex items-center text-[10px] uppercase tracking-widest text-taupe hover:text-theme-text disabled:opacity-0 transition-all font-bold px-6 py-3"
            >
              <ChevronLeft className="mr-2 h-4 w-4" /> Back
            </button>
            <button
               onClick={handleNext}
               disabled={!isInputValid}
               className="btn-gold px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
               {currentStep === auditData.length - 1 ? "See Results" : "Next Step"} 
               {currentStep !== auditData.length - 1 && <ArrowRight className="ml-2 h-4 w-4" />}
            </button>
      </div>
    </div>
  );
}
